import React, { useState, useEffect } from 'react';
import styles from './AddPartyAccountForm.module.css';

interface AddPartyAccountFormProps {
  onSubmit: (accountData: PartyAccountFormData) => void;
  onCancel: () => void;
  parties: Array<{ id: string; name: string }>;
  initialData?: Partial<PartyAccountFormData>;
}

export interface PartyAccountFormData {
  party: string;
  accountType: string;
  bankName: string;
  holderName: string;
  accountNo: string;
  ifscCode: string;
  branch: string;
  upI_ID: string;
  linkBankName: string;
  qrLabel: string;
  qrType: string;
  qrImageUrl: string;
  walletAddress: string;
  walletLabel: string;
  network: string;
  status: 'active' | 'inactive';
  notes: string;
}

const AddPartyAccountForm: React.FC<AddPartyAccountFormProps> = ({ 
  onSubmit, 
  onCancel, 
  parties,
  initialData 
}) => {
  const [formData, setFormData] = useState<PartyAccountFormData>({
    party: '',
    accountType: '1',
    bankName: '',
    holderName: '',
    accountNo: '',
    ifscCode: '',
    branch: '',
    upI_ID: '',
    linkBankName: '',
    qrLabel: '',
    qrType: '1',
    qrImageUrl: '',
    walletAddress: '',
    walletLabel: '',
    network: '1',
    status: 'active',
    notes: '',
  });

  // Populate form with initial data when editing
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Determine which fields to show based on account type
  const isBankAccount = formData.accountType === '1';
  const isUPI = formData.accountType === '2';
  const isQRCode = formData.accountType === '3';
  const isWallet = formData.accountType === '4' || formData.accountType === '5';

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      {/* Party and Account Type Row */}
      <div className={styles.row}>
        <div className={styles.formGroup}>
          <label htmlFor="party" className={styles.label}>
            Party <span className={styles.required}>*</span>
          </label>
          <select
            id="party"
            name="party"
            value={formData.party}
            onChange={handleChange}
            className={styles.select}
            required
          >
            <option value="">Select Party</option>
            {parties.map(party => (
              <option key={party.id} value={party.id}>{party.name}</option>
            ))}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="accountType" className={styles.label}>
            Account Type <span className={styles.required}>*</span>
          </label>
          <select
            id="accountType"
            name="accountType"
            value={formData.accountType}
            onChange={handleChange}
            className={styles.select}
            required
          >
            <option value="">Select Account Type</option>
            <option value="1">Bank Account</option>
            <option value="2">UPI</option>
            <option value="3">QR Code</option>
            <option value="4">Wallet</option>
            <option value="4">Wallet Address</option>
          </select>
        </div>
      </div>

      {/* Bank Name and Holder Name Row - Bank Account Only */}
      {isBankAccount && (
        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label htmlFor="bankName" className={styles.label}>Bank Name</label>
            <input
              id="bankName"
              type="text"
              name="bankName"
              value={formData.bankName}
              onChange={handleChange}
              className={styles.input}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="holderName" className={styles.label}>Holder Name</label>
            <input
              id="holderName"
              type="text"
              name="holderName"
              value={formData.holderName}
              onChange={handleChange}
              className={styles.input}
            />
          </div>
        </div>
      )}

      {/* Account No. and IFSC Code Row - Bank Account Only */}
      {isBankAccount && (
        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label htmlFor="accountNo" className={styles.label}>Account No.</label>
            <input
              id="accountNo"
              type="text"
              name="accountNo"
              value={formData.accountNo}
              onChange={handleChange}
              className={styles.input}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="ifscCode" className={styles.label}>IFSC Code</label>
            <input
              id="ifscCode"
              type="text"
              name="ifscCode"
              value={formData.ifscCode}
              onChange={handleChange}
              className={styles.input}
            />
          </div>
        </div>
      )}

      {/* Branch - Bank Account Only */}
      {isBankAccount && (
        <div className={styles.formGroup}>
          <label htmlFor="branch" className={styles.label}>Branch</label>
          <input
            id="branch"
            type="text"
            name="branch"
            value={formData.branch}
            onChange={handleChange}
            className={styles.input}
          />
        </div>
      )}

      {/* UPI Fields */}
      {isUPI && (
        <>
          <div className={styles.row}>
            <div className={styles.formGroup}>
              <label htmlFor="upI_ID" className={styles.label}>UPI ID</label>
              <input
                id="upI_ID"
                type="text"
                name="upI_ID"
                value={formData.upI_ID}
                onChange={handleChange}
                className={styles.input}
                placeholder="example@upi"
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="linkBankName" className={styles.label}>Linked Bank Name</label>
              <input
                id="linkBankName"
                type="text"
                name="linkBankName"
                value={formData.linkBankName}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
          </div>
        </>
      )}

      {/* QR Code Fields */}
      {isQRCode && (
        <>
          <div className={styles.row}>
            <div className={styles.formGroup}>
              <label htmlFor="qrLabel" className={styles.label}>QR Label</label>
              <input
                id="qrLabel"
                type="text"
                name="qrLabel"
                value={formData.qrLabel}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="qrType" className={styles.label}>QR Type</label>
              <select
                id="qrType"
                name="qrType"
                value={formData.qrType}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="1">UPI QR</option>
                <option value="2">Bank QR</option>
                <option value="2">Wallet QR</option>
              </select>
            </div>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="qrImageUrl" className={styles.label}>QR Image URL</label>
            <input
              id="qrImageUrl"
              type="text"
              name="qrImageUrl"
              value={formData.qrImageUrl}
              onChange={handleChange}
              className={styles.input}
              placeholder="Paste image URL here"
            />
          </div>
        </>
      )}

      {/* Wallet Fields */}
      {isWallet && (
        <>
          <div className={styles.formGroup}>
            <label htmlFor="walletAddress" className={styles.label}>Wallet Address</label>
            <input
              id="walletAddress"
              type="text"
              name="walletAddress"
              value={formData.walletAddress}
              onChange={handleChange}
              className={styles.input}
            />
          </div>
          <div className={styles.row}>
            <div className={styles.formGroup}>
              <label htmlFor="walletLabel" className={styles.label}>Wallet Label</label>
              <input
                id="walletLabel"
                type="text"
                name="walletLabel"
                value={formData.walletLabel}
                onChange={handleChange}
                className={styles.input}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="network" className={styles.label}>Network</label>
              <select
                id="network"
                name="network"
                value={formData.network}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="1">ERC-20</option>
                <option value="3">TRC-20</option>
                <option value="2">BEP-20</option>
                <option value="3">Solana</option>
                <option value="3">Polygon</option>
                <option value="3">Other</option>
              </select>
            </div>
          </div>
        </>
      )}

      {/* Status */}
      <div className={styles.formGroup}>
        <label htmlFor="status" className={styles.label}>Status</label>
        <select
          id="status"
          name="status"
          value={formData.status}
          onChange={handleChange}
          className={styles.select}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Notes */}
      <div className={styles.formGroup}>
        <label htmlFor="notes" className={styles.label}>Notes</label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          className={styles.textarea}
          rows={3}
        />
      </div>

      {/* Action Buttons */}
      <div className={styles.actions}>
        <button type="button" onClick={onCancel} className={styles.cancelButton}>
          Cancel
        </button>
        <button type="submit" className={styles.createButton}>
          {initialData ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default AddPartyAccountForm;
